import * as React from "react";
import { AccessUser, Branch, Region, SubscribedPlayerSearchResponse } from "../../../interfaces";
import { useEffect, useMemo, useState } from "react";
import { Header, Icon, Table, TableBody, TableHeader, TableHeaderCell, TableRow } from "semantic-ui-react";
import { EligibilityRule } from "./components/EligibilityRule";
import { EligibilityRadio } from "./components/EligibilityRadio";
import "./styles.scss";
import { CoachService, PlayersServices, AssistantServices, AccessType, GenericAccess } from "../../../services";
import { AccessListModal, AccessUserItem } from "./components/AccessListModal";
import { fullNameFrom, useSocialCoachSession } from "../../../utils";
import { SegmentOption, SegmentSelectionModal } from "./components/SegmentSelectionModal";
import { PlayerSelectionModal } from "./components/PlayerSelectionModal";
import { EligibilityCriterion, EligibilityLevel } from "@/services/bedrock";

interface OwnProps {
  originalAccess: GenericAccess;
  currentAccess: GenericAccess;
  accessType: AccessType;
  updateHandler: (newCriteria: EligibilityCriterion[]) => Promise<any>;
  isNew?: boolean;
}

type Props = OwnProps;

const EligibilityEditorFC: React.FC<Props> = ({ originalAccess, currentAccess, accessType, updateHandler, isNew }) => {
  const { coachId, userId } = useSocialCoachSession();
  const [loading, setLoading] = useState(false);
  const [criterion, setCriterion] = useState<EligibilityCriterion[]>(originalAccess.eligibilityCriteria || []);
  const [showRules, setShowRules] = useState(!!originalAccess.eligibilityCriteria?.length);
  const [showPlayerList, setShowPlayerList] = useState(false);
  const [showAssistantList, setShowAssistantList] = useState(false);
  const [showRegionChooser, setShowRegionChooser] = useState(false);
  const [showBranchChooser, setShowBranchChooser] = useState(false);
  const [showPlayerChooser, setShowPlayerChooser] = useState(false);
  const [allPlayers, setAllPlayers] = useState<SubscribedPlayerSearchResponse[]>();
  const [playerList, setPlayerList] = useState<AccessUserItem[] | undefined>();
  const [assistants, setAssistants] = useState<AccessUserItem[] | undefined>();
  const [branches, setBranches] = useState<Branch[] | undefined>();
  const [regions, setRegions] = useState<Region[] | undefined>();
  const [regionsLoading, setRegionsLoading] = useState<boolean>(false);
  const [branchesLoading, setBranchesLoading] = useState<boolean>(false);
  const [playersLoading, setPlayersLoading] = useState<boolean>(false);
  const usersWithAccess = useMemo(
    () => currentAccess.accessUsers?.filter(a => a.hasReadAccess),
    [currentAccess.accessUsers]
  );
  const originalUsersWithAccess = useMemo(
    () => originalAccess.accessUsers?.filter(a => a.hasReadAccess),
    [originalAccess.accessUsers]
  );
  const hasEligibilityAccess = useMemo(() => {
    return isNew || (!!userId && !!originalUsersWithAccess?.some(u => u.userId == userId && u.hasEligibilityAccess));
  }, [userId, originalUsersWithAccess, isNew]);

  useEffect(() => {
    setCriterion(originalAccess.eligibilityCriteria || []);
  }, [originalAccess.eligibilityCriteria]);

  useEffect(() => {
    setShowRules(!!originalAccess?.eligibilityCriteria?.length);
  }, [originalAccess?.eligibilityCriteria?.length]);

  useEffect(() => {
    loadPlayers(currentAccess.eligiblePlayerIds || []).catch(console.error);
  }, [currentAccess.eligiblePlayerIds, allPlayers]);

  useEffect(() => {
    loadBranches().catch(console.error);
    loadRegions().catch(console.error);
    loadAllPlayers().catch(console.error);
  }, [coachId]);

  useEffect(() => {
    loadAssistants(usersWithAccess || []).catch(console.error);
  }, [usersWithAccess]);

  const loadPlayers = async (playerIds: number[]) => {
    const newList = allPlayers?.filter(p => playerIds.includes(p.playerAccount.playerId!)) || [];
    setPlayerList(
      newList.map(player => {
        return {
          name: fullNameFrom(player.playerAccount),
          email: player.playerAccount.email,
          regionNames: player.regionNames,
          branchNames: player.branchNames,
          stateCodes: player.stateCodes,
        } as AccessUserItem;
      })
    );
  };

  const loadAssistants = async (users: AccessUser[]) => {
    const userIds = users.map(a => a.userId);
    const allAssistants = await AssistantServices.getByCoachId(coachId?.toString()!);
    const assistants = allAssistants.filter(a => userIds.includes(a.userId));
    setAssistants(
      users.map(accessUser => {
        const assistant = assistants.find(a => a.userId == accessUser.userId);
        return {
          name: accessUser.name,
          email: accessUser.email,
          regionNames: assistant?.regionNames,
          branchNames: assistant?.branchNames,
          readAccess: accessUser.hasReadAccess,
          writeAccess: accessUser.hasWriteAccess,
          eligibilityAccess: accessUser.hasEligibilityAccess,
        } as AccessUserItem;
      })
    );
  };

  const loadAllPlayers = async () => {
    setPlayersLoading(true);
    const list = await PlayersServices.searchSubscribedPlayersByTag(coachId?.toString()!);
    setAllPlayers(list.content);
    setPlayersLoading(false);
  };

  const loadBranches = async () => {
    setBranchesLoading(true);
    const list = await CoachService.branches(coachId?.toString()!);
    setBranches(list);
    setBranchesLoading(false);
  };

  const loadRegions = async () => {
    setRegionsLoading(true);
    const list = await CoachService.regions(coachId?.toString()!);
    setRegions(list);
    setRegionsLoading(false);
  };

  const updateCriterion = async (newCriterion: EligibilityCriterion[]) => {
    setCriterion(newCriterion);
    setLoading(true);
    await updateHandler(newCriterion);
    setLoading(false);
  };

  const diffString = (n1?: number, n2?: number): string => {
    const diff = (n1 || 0) - (n2 || 0);
    if (diff < 0) return `${n1} (${diff})`;
    else if (diff > 0) return `${n1} (+${diff})`;
    else return `${n1}`;
  };

  const typeLabel = useMemo(() => {
    switch (accessType) {
      case "CAMPAIGNS":
        return "Campaign";
      case "LIBRARY":
        return "Prompt";
      case "VIDEO_PAGES":
        return "Super Page";
    }
  }, [accessType]);

  const playerCount = useMemo(() => {
    return diffString(currentAccess.eligiblePlayerIds?.length || 0, originalAccess.eligiblePlayerIds?.length || 0);
  }, [currentAccess.eligiblePlayerIds?.length, originalAccess.eligiblePlayerIds?.length]);

  const assistantCount = useMemo(() => {
    return diffString(usersWithAccess?.length || 0, originalUsersWithAccess?.length || 0);
  }, [usersWithAccess?.length, originalUsersWithAccess?.length]);

  const hasChanges = useMemo(() => {
    return (
      usersWithAccess?.length != originalUsersWithAccess?.length ||
      currentAccess.eligiblePlayerIds?.length != originalAccess.eligiblePlayerIds?.length
    );
  }, [
    currentAccess.eligiblePlayerIds?.length,
    originalAccess.eligiblePlayerIds?.length,
    usersWithAccess?.length,
    originalUsersWithAccess?.length,
  ]);

  const updateRule = (newRule: EligibilityCriterion) => {
    const otherRules = criterion.filter(r => r.level != newRule.level);
    updateCriterion([...otherRules, newRule]).catch(console.error);
  };

  const clearRule = (level: EligibilityLevel) => {
    const otherRules = criterion.filter(r => r.level != level);
    updateCriterion([...otherRules]).catch(console.error);
  };

  const branchRule = useMemo(() => criterion.find(c => c.level == "BRANCH"), [criterion]);
  const branchTags = useMemo(() => {
    return (branchRule?.values || []).map(
      bid => branches?.find(b => b.branchId == bid.toString())?.name || `Branch ${bid}`
    );
  }, [branches, branchRule?.values]);
  const branchOptions = useMemo(() => {
    return (
      branches?.map(b => {
        return {
          id: Number(b.branchId),
          level: "BRANCH",
          name: b.name,
        } as SegmentOption;
      }) || []
    );
  }, [branches]);

  const regionRule = useMemo(() => criterion.find(c => c.level == "REGION"), [criterion]);
  const regionTags = useMemo(() => {
    return (regionRule?.values || []).map(
      rid => regions?.find(r => r.regionId == rid.toString())?.name || `Region ${rid}`
    );
  }, [regions, regionRule?.values]);
  const regionOptions = useMemo(() => {
    return (
      regions?.map(r => {
        return {
          id: Number(r.regionId),
          level: "REGION",
          name: r.name,
        } as SegmentOption;
      }) || []
    );
  }, [regions]);

  const playerRule = useMemo(() => criterion.find(c => c.level == "PLAYER_ID"), [criterion]);
  const playerTags = useMemo(() => {
    const maxToShow = 5;
    const playerCount = playerRule?.values?.length || 0;
    const playersToShow = (playerRule?.values || []).slice(0, maxToShow);
    const showMoreTag = playerCount > maxToShow;
    const tags = playersToShow.map(pid => {
      const player = allPlayers?.find(p => p.playerAccount.playerId == pid);
      return player ? fullNameFrom(player.playerAccount) : `Player ${pid}`;
    });
    if (showMoreTag) tags.push(`+${playerCount - maxToShow} Others`);
    return tags;
  }, [allPlayers, playerRule?.values]);

  const updateRuleFlag = (currentRule: EligibilityCriterion | undefined, level: EligibilityLevel, flag: boolean) => {
    if (currentRule) updateRule({ ...currentRule, isInclude: flag });
    else
      updateRule({
        isInclude: flag,
        level,
        values: [],
      });
  };

  const updateSelectedValues = (
    currentRule: EligibilityCriterion | undefined,
    level: EligibilityLevel,
    values: number[]
  ) => {
    if (values.length == 0) {
      clearRule(level);
    } else if (currentRule) updateRule({ ...currentRule, values });
    else
      updateRule({
        isInclude: true,
        level,
        values,
      });
  };

  const updateShowRules = (b: boolean) => {
    if (b) {
      setShowRules(false);
      updateCriterion([]).catch(console.error);
    } else {
      setShowRules(true);
      updateCriterion(originalAccess?.eligibilityCriteria || []).catch(console.error);
    }
  };

  return (
    <div className={"eligibilityEditor"}>
      <Header size={"small"} className={"flexHeader"}>
        Eligible Users
        {loading && <Icon loading name="spinner" className={"headerLoader"} />}
        {hasChanges && <div className={"unsavedChanges"}>*Unsaved changes</div>}
      </Header>
      <div>
        <b>Players</b>: {playerCount}{" "}
        <a onClick={() => setShowPlayerList(true)} className={"eligibilityStat"}>
          View List
        </a>
      </div>
      <div>
        <b>Assistants</b>: {assistantCount}{" "}
        <a onClick={() => setShowAssistantList(true)} className={"eligibilityStat"}>
          View List
        </a>
      </div>

      <Header size={"small"}>Rules</Header>
      <EligibilityRadio
        name={"primary-eligibility-toggle"}
        included={!showRules}
        onUpdate={updateShowRules}
        trueLabel={"Available to everyone"}
        falseLabel={"Set rules for eligibility"}
        readOnly={!hasEligibilityAccess}
      />
      {showRules && (
        <Table celled>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Type</TableHeaderCell>
              <TableHeaderCell>Rule</TableHeaderCell>
              <TableHeaderCell>Selected</TableHeaderCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            <EligibilityRule
              level={"REGION"}
              currentRule={regionRule}
              tags={regionTags}
              loading={regionsLoading}
              readOnly={!hasEligibilityAccess}
              onIncludeFlagChanged={f => updateRuleFlag(regionRule, "REGION", f)}
              onUpdateClicked={() => setShowRegionChooser(true)}
            />

            <EligibilityRule
              level={"BRANCH"}
              currentRule={branchRule}
              tags={branchTags}
              loading={branchesLoading}
              readOnly={!hasEligibilityAccess}
              onIncludeFlagChanged={f => updateRuleFlag(branchRule, "BRANCH", f)}
              onUpdateClicked={() => setShowBranchChooser(true)}
            />

            <EligibilityRule
              level={"PLAYER_ID"}
              currentRule={playerRule}
              tags={playerTags}
              loading={playersLoading}
              readOnly={!hasEligibilityAccess}
              onIncludeFlagChanged={f => updateRuleFlag(playerRule, "PLAYER_ID", f)}
              onUpdateClicked={() => setShowPlayerChooser(true)}
            />
          </TableBody>
        </Table>
      )}
      <AccessListModal
        title={`Players with access to this ${typeLabel}`}
        users={playerList || []}
        open={showPlayerList}
        onClose={() => setShowPlayerList(false)}
      />
      <AccessListModal
        title={`Assistants with access to this ${typeLabel}`}
        users={assistants || []}
        open={showAssistantList}
        onClose={() => setShowAssistantList(false)}
        showPermissions={true}
      />
      <SegmentSelectionModal
        open={showRegionChooser}
        level={"REGION"}
        options={regionOptions}
        values={regionRule?.values || []}
        onUpdate={values => {
          updateSelectedValues(regionRule, "REGION", values);
          setShowRegionChooser(false);
        }}
        onClose={() => setShowRegionChooser(false)}
      />
      <SegmentSelectionModal
        open={showBranchChooser}
        level={"BRANCH"}
        options={branchOptions}
        values={branchRule?.values || []}
        onUpdate={values => {
          updateSelectedValues(branchRule, "BRANCH", values);
          setShowBranchChooser(false);
        }}
        onClose={() => setShowBranchChooser(false)}
      />
      <PlayerSelectionModal
        open={showPlayerChooser}
        players={allPlayers || []}
        values={playerRule?.values || []}
        onUpdate={values => {
          updateSelectedValues(playerRule, "PLAYER_ID", values);
          setShowPlayerChooser(false);
        }}
        onClose={() => setShowPlayerChooser(false)}
      />
    </div>
  );
};

export const EligibilityEditor = EligibilityEditorFC;
